/**
 * ReturningScreen — Shown when the app reopens during a live retreat.
 *
 * The user must make an explicit choice: return to their practice, or end it.
 * No auto-dismiss. No countdown. The retreat waits for them.
 *
 * "resuming" — engine auto-resumed from wall-clock offset in this tab
 * "other-tab" — the same retreat is running in another browser window
 *
 * UI-INV-005: Leaf component, props only, no engine imports.
 */
import styles from "./ReturningScreen.module.css";
import { TEST_IDS } from "../testing/testIds";

export type ReturningMode = "resuming" | "other-tab";

type Props = {
  retreatName: string;
  mode: ReturningMode;
  containerName?: string;
  elapsedLabel?: string;
  onReturn: () => void;
  onEnd: () => void;
};

export function ReturningScreen({
  retreatName,
  mode,
  containerName,
  elapsedLabel,
  onReturn,
  onEnd,
}: Props) {
  const isOtherTab = mode === "other-tab";

  return (
    <div className={styles.screen} data-testid={TEST_IDS.RETURNING_SCREEN}>
      <div className={styles.inner}>
        <div className={styles.dot} />

        <div className={styles.eyebrow}>
          {isOtherTab ? "YOUR RETREAT IS ELSEWHERE" : "YOUR PRACTICE IS WAITING"}
        </div>

        <h1 className={styles.heading}>{retreatName}</h1>

        {!isOtherTab && (containerName || elapsedLabel) && (
          <p className={styles.sub}>
            {containerName}
            {containerName && elapsedLabel ? " · " : ""}
            {elapsedLabel}
          </p>
        )}

        {isOtherTab && (
          <p className={styles.note}>
            This retreat is running in another window.
          </p>
        )}

        <div className={styles.actions}>
          <button
            className={styles.returnBtn}
            onClick={onReturn}
            type="button"
            data-testid={TEST_IDS.RETURNING_RETURN_BTN}
          >
            {isOtherTab ? "Continue here" : "Return to practice"}
          </button>

          <button
            className={styles.endLink}
            onClick={onEnd}
            type="button"
            data-testid={TEST_IDS.RETURNING_END_BTN}
          >
            {isOtherTab ? "not now" : "end this retreat"}
          </button>
        </div>
      </div>
    </div>
  );
}
